import { ReactNode } from 'react';
import { create } from 'zustand';

import handleException from '@/common/utils/errors';

export type AlertType = 'success' | 'danger' | 'warning' | 'info';

export interface Alert {
  id: string;
  type: AlertType;
  title?: string;
  message: string;
  duration: number; // in seconds
  component?: ReactNode;
  showNotification?: boolean; // New prop for browser notifications
}

interface AlertStore {
  alerts: Alert[];
  notificationsPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<boolean>;
  showAlert: (alert: Omit<Alert, 'id'>) => void;
  removeAlert: (id: string) => void;
}

export const useAlertStore = create<AlertStore>((set) => ({
  alerts: [],
  notificationsPermission: Notification.permission,

  requestNotificationPermission: async () => {
    if (!('Notification' in window)) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      set({ notificationsPermission: permission });
      return permission === 'granted';
    } catch (error) {
      handleException(error);
      return false;
    }
  },

  showAlert: (alert) => {
    const id = Math.random().toString(36).substring(7);
    const newAlert = { ...alert, id };

    set((state) => ({
      alerts: [...state.alerts, newAlert],
    }));

    if (alert.showNotification && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(alert.title || 'Alert', {
        body: alert.message,
        icon: '/favicon.png',
        tag: id,
      });
    }

    // Remove alert after duration
    setTimeout(() => {
      set((state) => ({
        alerts: state.alerts.filter((a) => a.id !== id),
      }));
    }, alert.duration * 1000);
  },

  removeAlert: (id) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== id),
    }));
  },
}));
